import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Button,
  Divider,
  FormControlLabel,
  FormHelperText,
  Grid,
  Icon,
  LinearProgress,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  Snackbar
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import MuiAlert from '@material-ui/lab/Alert';
import parse from 'autosuggest-highlight/parse';
import match from 'autosuggest-highlight/match';
import {
  uploadPrivateImageApi,
  getAssetManagerImagesApi
} from 'api';
import {
  changeHomepageCTABlockConfig,
  changeHomepageCTACategory
} from 'redux/actions/store_configs';
import {
  uploadFileTypeUnsupported,
  uploadFileSizeLimit,
  uploadImageResolutionLimit
} from 'redux/actions/general';
import { ImageGalleryDialog } from 'components';
import { assetManagerUrl } from 'configs';


const MAX_CTA_FILE_SIZE = 250 * 1024;

const CTAHorizontalBottom = ({values, errors, setFieldValue}) => {
  const dispatch = useDispatch();
  const { horizontal_bottom } = values;
  const [openImageDialog, toggleOpenImageDialog] = useState(false);
  const [selectedImageValues, setSelectedImageValues] = useState({});
  const [openOwnDesktopImageDialog, toggleOpenOwnDesktopImageDialog] = useState(false);
  const [openOwnMobileImageDialog, toggleOpenOwnMobileImageDialog] = useState(false);
  const [ownDesktopImagesTagIds, setOwnDesktopImagesTagIds] = useState('');
  const [ownMobileImagesTagIds, setOwnMobileImagesTagIds] = useState('');

  const {
    storeActiveCategoriesWithActiveNavLevel,
    loadingCategories,
    errorCategories
  } = useSelector((state) => state.categories);

  const {
    assetCategoryTags,
    assetImagesData,
    currentCategoryId
  } = useSelector((state) => state.asset_manager);

  const {profile: {stores = []}} = useSelector((state) => state.user);

  const storeCode = stores.length && stores[0]?.storeCode;

  const horizontalBottomDesktopTagData = assetCategoryTags.find((tag) => tag.tagCode === 'block_3_skin_desktop');
  const horizontalBottomMobileTagData = assetCategoryTags.find((tag) => tag.tagCode === 'block_3_skin_mobile');
  const privateTagData = assetCategoryTags.find((tag) => tag.tagCode === 'private');
  const storeCodeTagData = assetCategoryTags.find((tag) => tag.tagCode === `${storeCode}`);

  useEffect(() => {
    if (horizontalBottomDesktopTagData && privateTagData && storeCodeTagData) {
      setOwnDesktopImagesTagIds(`${horizontalBottomDesktopTagData.id}, ${privateTagData.id}, ${storeCodeTagData.id}`);
    }

    if (horizontalBottomMobileTagData && privateTagData && storeCodeTagData) {
      setOwnMobileImagesTagIds(`${horizontalBottomMobileTagData.id}, ${privateTagData.id}, ${storeCodeTagData.id}`);
    }
  }, [horizontalBottomDesktopTagData, horizontalBottomMobileTagData, privateTagData, storeCodeTagData]);

  useEffect(() => {
    if (ownDesktopImagesTagIds) {
      getAssetManagerImagesApi({
        dispatch,
        limit: 18,
        page: 1,
        tagIds: ownDesktopImagesTagIds,
        isPrivate: true
      });
    }

    if (ownMobileImagesTagIds) {
      getAssetManagerImagesApi({
        dispatch,
        limit: 20,
        page: 1,
        tagIds: ownMobileImagesTagIds,
        isPrivate: true
      });
    }
  }, [ownDesktopImagesTagIds, ownMobileImagesTagIds]);

  const handleChange = (event) => {
    const {name, checked, type, value} = event.target;
    dispatch(changeHomepageCTABlockConfig(name, checked, type, value, 'horizontal_bottom'));
    setFieldValue(`horizontal_bottom[${name}]`, value);
  };

  const handleSelectCategory = (value, actionType, configTab, configPath) => {
    dispatch(changeHomepageCTACategory(actionType, value, configTab, configPath));
    setFieldValue(`horizontal_bottom[${configPath}]`, value ? value.id : '');
  };

  const handleSelectImage = (data, path) => {
    const value = `${assetManagerUrl}/${data?.webPath}`;
    dispatch(changeHomepageCTABlockConfig(
      path,
      null,
      'text',
      value,
      'horizontal_bottom'
    ));
    setFieldValue(`horizontal_bottom[${path}]`, value);
  };

  const handleUpLoadImage = async (event, imageType, path) => {
    const {files, value} = event.target;
    const formData = new FormData();
    const ownImagesTagIds = imageType === 'Desktop'
      ? ownDesktopImagesTagIds
      : ownMobileImagesTagIds;

    if (files.length) {
      const fileTypeDotIndexPosition = value.lastIndexOf('.') + 1;
      const slicedFileTypeFromFilePath = value.slice(fileTypeDotIndexPosition);
      const acceptableFormats = 'png|jpeg';
      const hasImageFile = new RegExp(`(image\\/(${acceptableFormats}))`, 'gi');

      const reader = new FileReader();

      reader.readAsDataURL(files[0]);
      reader.onload = (e) => {
        const image = new Image();

        image.src = e.target.result;
        image.onload = async () => {
          const {height, width} = image;

          if (!hasImageFile.test(files[0].type) && !hasImageFile.test(slicedFileTypeFromFilePath)) {
            dispatch(uploadFileTypeUnsupported());
          } else if (imageType === 'Desktop' && (width !== 554 || height !== 258)) {
            dispatch(uploadImageResolutionLimit('554 x 258px'));
          } else if (imageType === 'Mobile' && (width !== 380 || height !== 552)) {
            dispatch(uploadImageResolutionLimit('380 x 552px'));
          } else if (files[0].size > MAX_CTA_FILE_SIZE) {
            dispatch(uploadFileSizeLimit('250kB'));
          } else {
            formData.append('image', files[0]);

            if (ownImagesTagIds.length) {
              const tags = imageType === 'Desktop'
                ? `block_3_skin_desktop,private,${storeCode}`
                : `block_3_skin_mobile,private,${storeCode}`;

              formData.append('tags', tags);
            } else {
              const tagByResolutionType = imageType === 'Desktop'
                ? horizontalBottomDesktopTagData.tagCode
                : horizontalBottomMobileTagData.tagCode;

              const tags = [
                tagByResolutionType,
                privateTagData.tagCode,
                {
                  label: storeCode,
                  tagCode: storeCode,
                  category: currentCategoryId
                }
              ];
              formData.append('tags', JSON.stringify(tags));
            }

            const uploadedData = await uploadPrivateImageApi({
              dispatch,
              formData,
              isAddingNewTag: !ownImagesTagIds.length,
              categoryName: 'Storebuild-automaton'
            });

            if (uploadedData) {
              handleSelectImage(uploadedData, path);
            }
          }
        };
      };
    }
  };


  const isCustomLinkTitleError1 = errors?.horizontal_bottom?.['store_info/cta/cta_horizontal_bottom_mask_custom_link_title_1'];
  const isCustomLinkError1 = errors?.horizontal_bottom?.['store_info/cta/cta_horizontal_bottom_mask_custom_link_1'];
  const isCustomLinkTitleError2 = errors?.horizontal_bottom?.['store_info/cta/cta_horizontal_bottom_mask_custom_link_title_2'];
  const isCustomLinkError2 = errors?.horizontal_bottom?.['store_info/cta/cta_horizontal_bottom_mask_custom_link_2'];
  const isCustomLinkTitleError3 = errors?.horizontal_bottom?.['store_info/cta/cta_horizontal_bottom_mask_custom_link_title_3'];
  const isCustomLinkError3 = errors?.horizontal_bottom?.['store_info/cta/cta_horizontal_bottom_mask_custom_link_3'];
  const isCustomLinkTitleError4 = errors?.horizontal_bottom?.['store_info/cta/cta_horizontal_bottom_mask_custom_link_title_4'];
  const isCustomLinkError4 = errors?.horizontal_bottom?.['store_info/cta/cta_horizontal_bottom_mask_custom_link_4'];

  return (
    <div>
      <Typography variant="h4" className="my-6 mx-3">CTA Horizontal Bottom Block</Typography>
      <Divider className="mb-8" />
      <Grid container spacing={3} className="mb-4">
        <Grid item xs={12} sm={2} className="my-2 ml-4">Block Title</Grid>
        <Grid item xs={12} sm={9}>
          <TextField
            fullWidth
            variant="outlined"
            name={'store_info/cta/cta_horizontal_bottom_title'}
            label="Text"
            value={horizontal_bottom['store_info/cta/cta_horizontal_bottom_title']}
            onChange={handleChange}
            inputProps={{
              shrink: 'true'
            }}
          />
        </Grid>
        <Grid item xs={12} sm={2} className="my-2 ml-4">Block Mask Title</Grid>
        <Grid item xs={12} sm={9}>
          <TextField
            fullWidth
            variant="outlined"
            name={'store_info/cta/cta_horizontal_bottom_mask_title'}
            label="Text"
            value={horizontal_bottom['store_info/cta/cta_horizontal_bottom_mask_title']}
            onChange={handleChange}
            inputProps={{
              shrink: 'true'
            }}
          />
        </Grid>
        <Grid item xs={12} sm={2} className="my-2 ml-4">Link 1</Grid>
        <Grid item xs={12} sm={9}>
          <RadioGroup
            row
            aria-label={'select option 1'}
            name={'store_info/cta/is_cta_horizontal_bottom_mask_category_1'}
            value={horizontal_bottom['store_info/cta/is_cta_horizontal_bottom_mask_category_1']}
            onChange={handleChange}
            className="mb-2"
          >
            <FormControlLabel
              value="1"
              control={<Radio size="small" color="primary" />}
              label="Category"
            />
            <FormControlLabel
              value="0"
              control={<Radio size="small" color="primary" />}
              label="Custom"
            />
          </RadioGroup>
          {horizontal_bottom['store_info/cta/is_cta_horizontal_bottom_mask_category_1'] == 1 ?
            <>
              {loadingCategories && <LinearProgress className="mt-4" />}
              {!loadingCategories && errorCategories && <div>Can&apos;t fetch categories data</div>}
              {!loadingCategories && !errorCategories && storeActiveCategoriesWithActiveNavLevel.length &&
              <Autocomplete
                size="small"
                fullWidth
                options={storeActiveCategoriesWithActiveNavLevel}
                getOptionLabel={(option) => option.nameWithPath || ''}
                value={storeActiveCategoriesWithActiveNavLevel.find((category) => (
                  category.id == horizontal_bottom['store_info/cta/cta_horizontal_bottom_mask_categoryId_1']) || null
                )}
                getOptionSelected={(option) => (
                  horizontal_bottom['store_info/cta/cta_horizontal_bottom_mask_categoryId_1'] == option.id
                )}
                onChange={(_, value, action) => (
                  handleSelectCategory(
                    value,
                    action,
                    'horizontal_bottom',
                    'store_info/cta/cta_horizontal_bottom_mask_categoryId_1'
                  )
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    label="Category"
                  />
                )}
                renderOption={(option, { inputValue }) => {
                  const matches = match(option.nameWithPath, inputValue);
                  const parts = parse(option.nameWithPath, matches);
                  return (
                    <div>
                      {parts.map((part, index) => (
                        <span key={index} className={`${part.highlight ? 'text-primary font-bold' : ''}`}>
                          {part.text}
                        </span>
                      ))}
                    </div>
                  );
                }}
              />
              }
            </>
            :
            <>
              <TextField
                fullWidth
                variant="outlined"
                name={'store_info/cta/cta_horizontal_bottom_mask_custom_link_title_1'}
                label="Title"
                value={
                  horizontal_bottom['store_info/cta/cta_horizontal_bottom_mask_custom_link_title_1'] !== null
                    ? horizontal_bottom['store_info/cta/cta_horizontal_bottom_mask_custom_link_title_1']
                    : ''
                }
                onChange={handleChange}
                className="mb-5"
                inputProps={{
                  shrink: 'true'
                }}
                error={isCustomLinkTitleError1}
                helperText={
                  isCustomLinkTitleError1 &&
                  errors.horizontal_bottom['store_info/cta/cta_horizontal_bottom_mask_custom_link_title_1']
                }
              />
              <TextField
                fullWidth
                variant="outlined"
                name={'store_info/cta/cta_horizontal_bottom_mask_custom_link_1'}
                label="Link"
                value={
                  horizontal_bottom['store_info/cta/cta_horizontal_bottom_mask_custom_link_1'] !== null
                    ? horizontal_bottom['store_info/cta/cta_horizontal_bottom_mask_custom_link_1']
                    : ''
                }
                onChange={handleChange}
                inputProps={{
                  shrink: 'true'
                }}
                error={isCustomLinkError1}
                helperText={
                  isCustomLinkError1 &&
                  errors.horizontal_bottom['store_info/cta/cta_horizontal_bottom_mask_custom_link_1']
                }
              />
            </>
          }
        </Grid>
        <Grid item xs={12} sm={2} className="my-2 ml-4">Link 2</Grid>
        <Grid item xs={12} sm={9}>
          <RadioGroup
            row
            aria-label={'select option 2'}
            name={'store_info/cta/is_cta_horizontal_bottom_mask_category_2'}
            value={horizontal_bottom['store_info/cta/is_cta_horizontal_bottom_mask_category_2']}
            onChange={handleChange}
            className="mb-2"
          >
            <FormControlLabel
              value="1"
              control={<Radio size="small" color="primary" />}
              label="Category"
            />
            <FormControlLabel
              value="0"
              control={<Radio size="small" color="primary" />}
              label="Custom"
            />
          </RadioGroup>
          {horizontal_bottom['store_info/cta/is_cta_horizontal_bottom_mask_category_2'] == 1 ?
            <>
              {loadingCategories && <LinearProgress className="mt-4" />}
              {!loadingCategories && errorCategories && <div>Can&apos;t fetch categories data</div>}
              {!loadingCategories && !errorCategories && storeActiveCategoriesWithActiveNavLevel.length &&
              <Autocomplete
                size="small"
                fullWidth
                options={storeActiveCategoriesWithActiveNavLevel}
                getOptionLabel={(option) => option.nameWithPath || ''}
                value={storeActiveCategoriesWithActiveNavLevel.find((category) => (
                  category.id == horizontal_bottom['store_info/cta/cta_horizontal_bottom_mask_categoryId_2']) || null
                )}
                getOptionSelected={(option) => (
                  horizontal_bottom['store_info/cta/cta_horizontal_bottom_mask_categoryId_2'] == option.id
                )}
                onChange={(_, value, action) => (
                  handleSelectCategory(
                    value,
                    action,
                    'horizontal_bottom',
                    'store_info/cta/cta_horizontal_bottom_mask_categoryId_2'
                  )
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    label="Category"
                  />
                )}
                renderOption={(option, { inputValue }) => {
                  const matches = match(option.nameWithPath, inputValue);
                  const parts = parse(option.nameWithPath, matches);
                  return (
                    <div>
                      {parts.map((part, index) => (
                        <span key={index} className={`${part.highlight ? 'text-primary font-bold' : ''}`}>
                          {part.text}
                        </span>
                      ))}
                    </div>
                  );
                }}
              />
              }
            </>
            :
            <>
              <TextField
                fullWidth
                variant="outlined"
                name={'store_info/cta/cta_horizontal_bottom_mask_custom_link_title_2'}
                label="Title"
                value={
                  horizontal_bottom['store_info/cta/cta_horizontal_bottom_mask_custom_link_title_2'] !== null
                    ? horizontal_bottom['store_info/cta/cta_horizontal_bottom_mask_custom_link_title_2']
                    : ''
                }
                onChange={handleChange}
                className="mb-5"
                inputProps={{
                  shrink: 'true'
                }}
                error={isCustomLinkTitleError2}
                helperText={
                  isCustomLinkTitleError2 &&
                  errors.horizontal_bottom['store_info/cta/cta_horizontal_bottom_mask_custom_link_title_2']
                }
              />
              <TextField
                fullWidth
                variant="outlined"
                name={'store_info/cta/cta_horizontal_bottom_mask_custom_link_2'}
                label="Link"
                value={
                  horizontal_bottom['store_info/cta/cta_horizontal_bottom_mask_custom_link_2'] !== null
                    ? horizontal_bottom['store_info/cta/cta_horizontal_bottom_mask_custom_link_2']
                    : ''
                }
                onChange={handleChange}
                inputProps={{
                  shrink: 'true'
                }}
                error={isCustomLinkError2}
                helperText={
                  isCustomLinkError2 &&
                  errors.horizontal_bottom['store_info/cta/cta_horizontal_bottom_mask_custom_link_2']
                }
              />
            </>
          }
        </Grid>
        <Grid item xs={12} sm={2} className="my-2 ml-4">Link 3</Grid>
        <Grid item xs={12} sm={9}>
          <RadioGroup
            row
            aria-label={'select option 3'}
            name={'store_info/cta/is_cta_horizontal_bottom_mask_category_3'}
            value={horizontal_bottom['store_info/cta/is_cta_horizontal_bottom_mask_category_3']}
            onChange={handleChange}
            className="mb-2"
          >
            <FormControlLabel
              value="1"
              control={<Radio size="small" color="primary" />}
              label="Category"
            />
            <FormControlLabel
              value="0"
              control={<Radio size="small" color="primary" />}
              label="Custom"
            />
          </RadioGroup>
          {horizontal_bottom['store_info/cta/is_cta_horizontal_bottom_mask_category_3'] == 1 ?
            <>
              {loadingCategories && <LinearProgress className="mt-4" />}
              {!loadingCategories && errorCategories && <div>Can&apos;t fetch categories data</div>}
              {!loadingCategories && !errorCategories && storeActiveCategoriesWithActiveNavLevel.length &&
              <Autocomplete
                size="small"
                fullWidth
                options={storeActiveCategoriesWithActiveNavLevel}
                getOptionLabel={(option) => option.nameWithPath || ''}
                value={storeActiveCategoriesWithActiveNavLevel.find((category) => (
                  category.id == horizontal_bottom['store_info/cta/cta_horizontal_bottom_mask_categoryId_3']) || null
                )}
                getOptionSelected={(option) => (
                  horizontal_bottom['store_info/cta/cta_horizontal_bottom_mask_categoryId_3'] == option.id
                )}
                onChange={(_, value, action) => (
                  handleSelectCategory(
                    value,
                    action,
                    'horizontal_bottom',
                    'store_info/cta/cta_horizontal_bottom_mask_categoryId_3'
                  )
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    label="Category"
                  />
                )}
                renderOption={(option, { inputValue }) => {
                  const matches = match(option.nameWithPath, inputValue);
                  const parts = parse(option.nameWithPath, matches);
                  return (
                    <div>
                      {parts.map((part, index) => (
                        <span key={index} className={`${part.highlight ? 'text-primary font-bold' : ''}`}>
                          {part.text}
                        </span>
                      ))}
                    </div>
                  );
                }}
              />
              }
            </>
            :
            <>
              <TextField
                fullWidth
                variant="outlined"
                name={'store_info/cta/cta_horizontal_bottom_mask_custom_link_title_3'}
                label="Title"
                value={
                  horizontal_bottom['store_info/cta/cta_horizontal_bottom_mask_custom_link_title_3'] !== null
                    ? horizontal_bottom['store_info/cta/cta_horizontal_bottom_mask_custom_link_title_3']
                    : ''
                }
                onChange={handleChange}
                className="mb-5"
                inputProps={{
                  shrink: 'true'
                }}
                error={isCustomLinkTitleError3}
                helperText={
                  isCustomLinkTitleError3 &&
                  errors.horizontal_bottom['store_info/cta/cta_horizontal_bottom_mask_custom_link_title_3']
                }
              />
              <TextField
                fullWidth
                variant="outlined"
                name={'store_info/cta/cta_horizontal_bottom_mask_custom_link_3'}
                label="Link"
                value={
                  horizontal_bottom['store_info/cta/cta_horizontal_bottom_mask_custom_link_3'] !== null
                    ? horizontal_bottom['store_info/cta/cta_horizontal_bottom_mask_custom_link_3']
                    : ''
                }
                onChange={handleChange}
                inputProps={{
                  shrink: 'true'
                }}
                error={isCustomLinkError3}
                helperText={
                  isCustomLinkError3 &&
                  errors.horizontal_bottom['store_info/cta/cta_horizontal_bottom_mask_custom_link_3']
                }
              />
            </>
          }
        </Grid>
        <Grid item xs={12} sm={2} className="my-2 ml-4">Link 4</Grid>
        <Grid item xs={12} sm={9}>
          <RadioGroup
            row
            aria-label={'select option 4'}
            name={'store_info/cta/is_cta_horizontal_bottom_mask_category_4'}
            value={horizontal_bottom['store_info/cta/is_cta_horizontal_bottom_mask_category_4']}
            onChange={handleChange}
            className="mb-2"
          >
            <FormControlLabel
              value="1"
              control={<Radio size="small" color="primary" />}
              label="Category"
            />
            <FormControlLabel
              value="0"
              control={<Radio size="small" color="primary" />}
              label="Custom"
            />
          </RadioGroup>
          {horizontal_bottom['store_info/cta/is_cta_horizontal_bottom_mask_category_4'] == 1 ?
            <>
              {loadingCategories && <LinearProgress className="mt-4" />}
              {!loadingCategories && errorCategories && <div>Can&apos;t fetch categories data</div>}
              {!loadingCategories && !errorCategories && storeActiveCategoriesWithActiveNavLevel.length &&
              <Autocomplete
                size="small"
                fullWidth
                options={storeActiveCategoriesWithActiveNavLevel}
                getOptionLabel={(option) => option.nameWithPath || ''}
                value={storeActiveCategoriesWithActiveNavLevel.find((category) => (
                  category.id == horizontal_bottom['store_info/cta/cta_horizontal_bottom_mask_categoryId_4']) || null
                )}
                getOptionSelected={(option) => (
                  horizontal_bottom['store_info/cta/cta_horizontal_bottom_mask_categoryId_4'] == option.id
                )}
                onChange={(_, value, action) => (
                  handleSelectCategory(
                    value,
                    action,
                    'horizontal_bottom',
                    'store_info/cta/cta_horizontal_bottom_mask_categoryId_4'
                  )
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    label="Category"
                  />
                )}
                renderOption={(option, { inputValue }) => {
                  const matches = match(option.nameWithPath, inputValue);
                  const parts = parse(option.nameWithPath, matches);
                  return (
                    <div>
                      {parts.map((part, index) => (
                        <span key={index} className={`${part.highlight ? 'text-primary font-bold' : ''}`}>
                          {part.text}
                        </span>
                      ))}
                    </div>
                  );
                }}
              />
              }
            </>
            :
            <>
              <TextField
                fullWidth
                variant="outlined"
                name={'store_info/cta/cta_horizontal_bottom_mask_custom_link_title_4'}
                label="Title"
                value={
                  horizontal_bottom['store_info/cta/cta_horizontal_bottom_mask_custom_link_title_4'] !== null
                    ? horizontal_bottom['store_info/cta/cta_horizontal_bottom_mask_custom_link_title_4']
                    : ''
                }
                onChange={handleChange}
                className="mb-5"
                inputProps={{
                  shrink: 'true'
                }}
                error={isCustomLinkTitleError4}
                helperText={
                  isCustomLinkTitleError4 &&
                  errors.horizontal_bottom['store_info/cta/cta_horizontal_bottom_mask_custom_link_title_4']
                }
              />
              <TextField
                fullWidth
                variant="outlined"
                name={'store_info/cta/cta_horizontal_bottom_mask_custom_link_4'}
                label="Link"
                value={
                  horizontal_bottom['store_info/cta/cta_horizontal_bottom_mask_custom_link_4'] !== null
                    ? horizontal_bottom['store_info/cta/cta_horizontal_bottom_mask_custom_link_4']
                    : ''
                }
                onChange={handleChange}
                inputProps={{
                  shrink: 'true'
                }}
                error={isCustomLinkError4}
                helperText={
                  isCustomLinkError4 &&
                  errors.horizontal_bottom['store_info/cta/cta_horizontal_bottom_mask_custom_link_4']
                }
              />
            </>
          }
        </Grid>
        <Grid item xs={12} sm={2} className="my-2 ml-4">Block Button Category</Grid>
        <Grid item xs={12} sm={9}>
          {loadingCategories && <LinearProgress className="mt-4" />}
          {!loadingCategories && errorCategories && <div>Can&apos;t fetch categories data</div>}
          {!loadingCategories && !errorCategories && storeActiveCategoriesWithActiveNavLevel.length &&
            <Autocomplete
              size="small"
              fullWidth
              options={storeActiveCategoriesWithActiveNavLevel}
              getOptionLabel={(option) => option.nameWithPath || ''}
              value={storeActiveCategoriesWithActiveNavLevel.find((category) => (
                category.id == horizontal_bottom['store_info/cta/cta_horizontal_bottom_mask_button_categoryId']) || null
              )}
              getOptionSelected={(option) => (
                horizontal_bottom['store_info/cta/cta_horizontal_bottom_mask_button_categoryId'] == option.id
              )}
              onChange={(_, value, action) => (
                handleSelectCategory(
                  value,
                  action,
                  'horizontal_bottom',
                  'store_info/cta/cta_horizontal_bottom_mask_button_categoryId'
                )
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  label="Category"
                />
              )}
              renderOption={(option, { inputValue }) => {
                const matches = match(option.nameWithPath, inputValue);
                const parts = parse(option.nameWithPath, matches);
                return (
                  <div>
                    {parts.map((part, index) => (
                      <span key={index} className={`${part.highlight ? 'text-primary font-bold' : ''}`}>
                        {part.text}
                      </span>
                    ))}
                  </div>
                );
              }}
            />
          }
        </Grid>
        <Grid item xs={12} sm={2} className="my-2 ml-4">Block Image Desktop</Grid>
        <Grid item xs={12} sm={9}>
          <div>
            <Button
              className="w-full max-w-300"
              variant="outlined"
              color="primary"
              size="large"
            >
              <label htmlFor="upload-desktop-image-bottom" className="flex items-center">
                <Icon className="mr-2">upload</Icon>
                Upload Own Desktop Image
              </label>
            </Button>
            <input
              className="hidden"
              onChange={(event) => handleUpLoadImage(event, 'Desktop', 'dermpro/design/cta_image_3_desktop')}
              onClick={(event) => event.target.value = null}
              id="upload-desktop-image-bottom"
              type="file"
              accept="image/png, image/jpeg"
            />
          </div>
          <FormHelperText className="mt-2">
            <div>Files supported: png, jpeg, jpg. File size should be less than 250kB.</div>
            <div>Image resolution should be exactly 554x258px.</div>
          </FormHelperText>
          <Button
            className="w-full max-w-300 mt-2 mr-2"
            variant="outlined"
            color="primary"
            size="large"
            onClick={() => {
              toggleOpenImageDialog(true);
              setSelectedImageValues({
                tagId: horizontalBottomDesktopTagData.id,
                path: 'dermpro/design/cta_image_3_desktop',
                cols: 3,
                rowHeight: 160,
                type: 'Desktop'
              });
            }}
          >
            <Icon className="mr-2">wallpaper</Icon>
            Select Image From Library
          </Button>
          <Button
            className="w-full max-w-300 mt-2 mr-2 bg-periwinkle-gray"
            variant="outlined"
            color="primary"
            size="large"
            onClick={() => {
              toggleOpenOwnDesktopImageDialog(true);
              setSelectedImageValues({
                tagId: ownDesktopImagesTagIds,
                path: 'dermpro/design/cta_image_3_desktop',
                cols: 3,
                rowHeight: 160,
                type: 'Desktop'
              });
            }}
          >
            <Icon className="mr-2">wallpaper</Icon>
            Select From Own Images
          </Button>
        </Grid>
        <Grid item xs={12} sm={2} className="my-2 ml-4">Block Image Mobile</Grid>
        <Grid item xs={12} sm={9}>
          <div>
            <Button
              className="w-full max-w-300"
              variant="outlined"
              color="primary"
              size="large"
            >
              <label htmlFor="upload-mobile-image-bottom" className="flex items-center">
                <Icon className="mr-2">upload</Icon>
                Upload Own Mobile Image
              </label>
            </Button>
            <input
              className="hidden"
              onChange={(event) => handleUpLoadImage(event, 'Mobile', 'dermpro/design/cta_image_3_mobile')}
              onClick={(event) => event.target.value = null}
              id="upload-mobile-image-bottom"
              type="file"
              accept="image/png, image/jpeg"
            />
          </div>
          <FormHelperText className="mt-2">
            <div>Files supported: png, jpeg, jpg. File size should be less than 250kB.</div>
            <div>Image resolution should be exactly 380x552px.</div>
          </FormHelperText>
          <Button
            className="w-full max-w-300 mt-2 mr-2"
            variant="outlined"
            color="primary"
            size="large"
            onClick={() => {
              toggleOpenImageDialog(true);
              setSelectedImageValues({
                tagId: horizontalBottomMobileTagData.id,
                path: 'dermpro/design/cta_image_3_mobile',
                cols: 4,
                rowHeight: 340,
                type: 'Mobile'
              });
            }}
          >
            <Icon className="mr-2">wallpaper</Icon>
            Select Image From Library
          </Button>
          <Button
            className="w-full max-w-300 mt-2 mr-2 bg-periwinkle-gray"
            variant="outlined"
            color="primary"
            size="large"
            onClick={() => {
              toggleOpenOwnMobileImageDialog(true);
              setSelectedImageValues({
                tagId: ownMobileImagesTagIds,
                path: 'dermpro/design/cta_image_3_mobile',
                cols: 4,
                rowHeight: 340,
                type: 'Mobile'
              });
            }}
          >
            <Icon className="mr-2">wallpaper</Icon>
            Select From Own Images
          </Button>
        </Grid>
      </Grid>
      <Divider className="my-8" />
      {openImageDialog && selectedImageValues?.tagId &&
        <ImageGalleryDialog
          title={`Select Horizontal Bottom Block CTA ${selectedImageValues.type} Image From Library`}
          open={openImageDialog}
          setOpen={toggleOpenImageDialog}
          onImageSelect={(data) => handleSelectImage(data, selectedImageValues.path)}
          imageTagId={selectedImageValues.tagId}
          cols={selectedImageValues.cols}
          rowHeight={selectedImageValues.rowHeight}
        />
      }
      {(openOwnDesktopImageDialog && ownDesktopImagesTagIds && assetImagesData[ownDesktopImagesTagIds]?.items?.length) ?
        <ImageGalleryDialog
          title="Select Desktop CTA Image From Own Images"
          open={openOwnDesktopImageDialog}
          setOpen={toggleOpenOwnDesktopImageDialog}
          onImageSelect={(data) => handleSelectImage(data, selectedImageValues.path)}
          imageTagId={ownDesktopImagesTagIds}
          isPrivate
          cols={3}
          rowHeight={160}
        />
        :
        <Snackbar
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          autoHideDuration={3000}
          onClose={() => toggleOpenOwnDesktopImageDialog(false)}
          open={openOwnDesktopImageDialog}
        >
          <MuiAlert elevation={6} variant="filled" severity="warning">
            You have no your own uploaded images.
            Firstly, you have to upload at least one image.
          </MuiAlert>
        </Snackbar>
      }
      {(openOwnMobileImageDialog && ownMobileImagesTagIds && assetImagesData[ownMobileImagesTagIds]?.items?.length) ?
        <ImageGalleryDialog
          title="Select Mobile CTA Image From Own Images"
          open={openOwnMobileImageDialog}
          setOpen={toggleOpenOwnMobileImageDialog}
          onImageSelect={(data) => handleSelectImage(data, selectedImageValues.path)}
          imageTagId={ownMobileImagesTagIds}
          isPrivate
          cols={4}
          rowHeight={340}
        />
        :
        <Snackbar
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          autoHideDuration={3000}
          onClose={() => toggleOpenOwnMobileImageDialog(false)}
          open={openOwnMobileImageDialog}
        >
          <MuiAlert elevation={6} variant="filled" severity="warning">
            You have no your own uploaded images.
            Firstly, you have to upload at least one image.
          </MuiAlert>
        </Snackbar>
      }
    </div>
  );
};

export default CTAHorizontalBottom;
